import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Shield, X, Settings2, ChevronDown, ChevronUp } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'cookie_consent_v1';
// KVKK modal cache key — keep in sync so accepting either banner hides
// the other for login users (avoids the "GDPR popup keeps appearing"
// bug where the cookie banner stayed visible after consenting via the
// account-level KVKK modal).
const KVKK_LOCAL_FLAG = 'kvkk_consent_v1';

// Best-effort persist consent on the backend if the user is logged in,
// so the consent survives device / browser changes and admins can audit.
const syncToBackend = async (analytics, marketing) => {
  try {
    await axios.post(`${API}/kvkk/give-consent`, {
      consents: {
        aydinlatma_metni: true,
        kullanim_kosullari: true,
        cerez_analitik: !!analytics,
        pazarlama_iletisim: !!marketing,
        ucuncu_taraf_paylasim: !!marketing,
        profil_gorunurluk: true,
      },
      version: "1.0",
    });
  } catch (_) {
    // Anonymous user → 401 expected, ignore. Auth errors otherwise are
    // non-blocking for the cookie banner.
  }
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,   // always true
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY)
      || localStorage.getItem(KVKK_LOCAL_FLAG);
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = useCallback(() => {
    const now = new Date().toISOString();
    const consent = { essential: true, analytics: true, marketing: true, timestamp: now };
    try { localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent)); } catch (_) {}
    try { localStorage.setItem(KVKK_LOCAL_FLAG, JSON.stringify({ accepted: true, timestamp: now })); } catch (_) {}
    syncToBackend(true, true);
    setVisible(false);
  }, []);

  const handleRejectOptional = useCallback(() => {
    const now = new Date().toISOString();
    const consent = { essential: true, analytics: false, marketing: false, timestamp: now };
    try { localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent)); } catch (_) {}
    try { localStorage.setItem(KVKK_LOCAL_FLAG, JSON.stringify({ accepted: true, timestamp: now })); } catch (_) {}
    syncToBackend(false, false);
    setVisible(false);
  }, []);

  const handleSavePreferences = useCallback(() => {
    const now = new Date().toISOString();
    const consent = { ...preferences, timestamp: now };
    try { localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent)); } catch (_) {}
    try { localStorage.setItem(KVKK_LOCAL_FLAG, JSON.stringify({ accepted: true, timestamp: now })); } catch (_) {}
    syncToBackend(preferences.analytics, preferences.marketing);
    setVisible(false);
  }, [preferences]);

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] animate-in slide-in-from-bottom duration-500"
      data-testid="cookie-banner"
    >
      <div className="bg-white border-t-2 border-emerald-800 shadow-2xl mx-auto max-w-screen-xl">
        <div className="p-4 md:p-5">
          {/* Main content */}
          <div className="flex items-start gap-4">
            <Shield className="w-8 h-8 text-emerald-800 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-sm text-slate-900">
                We use cookies
              </h3>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                We use cookies to give you the best experience on our site, in line with the UK GDPR and the Privacy and Electronic Communications Regulations.
                Strictly necessary cookies are required for the site to work. Analytics and marketing cookies are only set with your consent.{' '}
                <a href="/page/cerez-politikasi" className="underline text-emerald-800 hover:text-emerald-600">
                  Cookie Policy
                </a>
              </p>
            </div>
            <button
              onClick={handleRejectOptional}
              className="text-slate-400 hover:text-slate-600 flex-shrink-0"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Detailed preferences */}
          {expanded && (
            <div className="mt-4 pt-4 border-t space-y-3" data-testid="cookie-preferences">
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium">Strictly necessary</p>
                  <p className="text-xs text-slate-500">Session, security (always active)</p>
                </div>
                <div className="bg-emerald-100 text-emerald-800 text-xs px-2 py-1 rounded">Required</div>
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium">Analytics</p>
                  <p className="text-xs text-slate-500">Anonymous usage statistics</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences(p => ({ ...p, analytics: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-slate-200 peer-focus:ring-2 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-600"></div>
                </label>
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium">Marketing</p>
                  <p className="text-xs text-slate-500">Personalised content and ads</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences(p => ({ ...p, marketing: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-slate-200 peer-focus:ring-2 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-600"></div>
                </label>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex items-center gap-3 mt-4">
            <Button
              onClick={handleAcceptAll}
              className="bg-emerald-800 hover:bg-emerald-900 text-white text-xs px-4 py-2"
              data-testid="cookie-accept-all"
            >
              Accept All
            </Button>
            <Button
              variant="outline"
              onClick={handleRejectOptional}
              className="text-xs px-4 py-2"
              data-testid="cookie-reject-optional"
            >
              Essential Only
            </Button>
            {expanded ? (
              <Button
                variant="outline"
                onClick={handleSavePreferences}
                className="text-xs px-4 py-2"
                data-testid="cookie-save-preferences"
              >
                Save Preferences
              </Button>
            ) : null}
            <button
              onClick={() => setExpanded(e => !e)}
              className="ml-auto flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
              data-testid="cookie-toggle-details"
            >
              <Settings2 className="w-3.5 h-3.5" />
              {expanded ? 'Hide' : 'Manage Preferences'}
              {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
